export const FEATURES = [
    {
        icon: "FileText",
        title: "Tender Summary",
        desc: "Get the entire tender condensed into a clear, structured summary in seconds.",
    },
    {
        icon: "ShieldCheck",
        title: "Eligibility Analysis",
        desc: "Instantly know if your business qualifies based on turnover, experience, and licenses.",
    },
    {
        icon: "ListChecks",
        title: "Required Documents Checklist",
        desc: "Auto-extracted checklist of every document you'll need to submit.",
    },
    {
        icon: "Coins",
        title: "EMD Detection",
        desc: "Detect EMD amount, payment mode, and deadline directly from the PDF.",
    },
    {
        icon: "CalendarClock",
        title: "Deadline Extraction",
        desc: "Never miss a submission. We pull every important date from the document.",
    },
    {
        icon: "AlertTriangle",
        title: "Risk Analysis",
        desc: "Identify hidden penalty clauses, unfavorable terms, and high-risk requirements.",
    },
    {
        icon: "Languages",
        title: "All Language Summary",
        desc: "Get the summary in any supported language — built for businesses across regions.",
    },
    {
        icon: "Download",
        title: "PDF Report Download",
        desc: "Download a beautifully formatted PDF report to share with your team.",
    },
    {
        icon: "Target",
        title: "Tender Match Score",
        desc: "A 0–100 score showing how well your business fits this specific tender.",
    },
    {
        icon: "Sparkles",
        title: "AI Bid Readiness",
        desc: "Get a step-by-step plan to prepare a winning bid before the deadline.",
    },
];

export const HOW_IT_WORKS = [
    {
        step: "01",
        title: "Upload Tender PDF",
        desc: "Drag and drop any tender document — government or private. PDFs up to 50MB.",
    },
    {
        step: "02",
        title: "AI Reads the Document",
        desc: "Our specialized models scan all pages, schedules, and annexures in under 60 seconds.",
    },
    {
        step: "03",
        title: "Get Eligibility, Risks & Summary",
        desc: "Receive a complete report with eligibility, EMD, risks, and an all-language summary.",
    },
    {
        step: "04",
        title: "Download Report",
        desc: "Export a clean PDF or save it to your dashboard to track bids over time.",
    },
];

export const PLANS = [
    {
        id: "starter",
        name: "Starter",
        price: 299,
        tagline: "Best for beginners exploring tenders",
        credits: "10 Tender Analyses / month",
        cta: "Start with Starter",
        popular: false,
        features: [
            "10 Tender Analyses per month",
            "PDF Summary",
            "All Language Summary",
            "Basic Document Extraction",
            "Email Support",
            "Tender Match Score",
        ],
    },
    {
        id: "pro",
        name: "Pro",
        price: 499,
        tagline: "Best value for growing businesses",
        credits: "30 Tender Analyses / month",
        cta: "Get Pro",
        popular: true,
        features: [
            "30 Tender Analyses per month",
            "Everything in Starter",
            "Advanced Risk Detection",
            "Better Extraction Accuracy",
            "Downloadable PDF Reports",
            "Priority Email Support",
            "AI Bid Readiness Plan",
        ],
    },
    {
        id: "business",
        name: "Business",
        price: 999,
        tagline: "For agencies & frequent bidders",
        credits: "Unlimited Tender Analyses",
        cta: "Go Business",
        popular: false,
        features: [
            "Unlimited Tender Analyses",
            "Everything in Pro",
            "Advanced AI Insights",
            "Full Premium Access",
            "Dedicated Account Manager",
            "Phone + Priority Support",
            "API Access (coming soon)",
        ],
    },
];

export const COMPARISON_ROWS = [
    { label: "Tender Analyses / month", starter: "10", pro: "30", business: "Unlimited" },
    { label: "All Language Summary", starter: true, pro: true, business: true },
    { label: "PDF Report Download", starter: true, pro: true, business: true },
    { label: "Advanced Risk Detection", starter: false, pro: true, business: true },
    { label: "AI Bid Readiness", starter: false, pro: true, business: true },
    { label: "Priority Support", starter: false, pro: true, business: true },
    { label: "Dedicated Manager", starter: false, pro: false, business: true },
    { label: "API Access", starter: false, pro: false, business: true },
];

export const TESTIMONIALS = [
    {
        name: "Rajesh Kumar",
        role: "Civil Contractor, Jaipur",
        image:
            "https://images.unsplash.com/photo-1606384682764-c3065dbcaf85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjBjb25zdHJ1Y3Rpb24lMjB3b3JrZXIlMjBjb250cmFjdG9yJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc4MTA3NzYxN3ww&ixlib=rb-4.1.0&q=85",
        quote:
            "I used to spend 3-4 hours reading every tender. TenderTrackAI tells me in 2 minutes whether I'm eligible. It saved me ₹50,000 in missed EMD deadlines last month alone.",
    },
    {
        name: "Priya Sharma",
        role: "Supplier, Maxwell Industries",
        image:
            "https://images.unsplash.com/photo-1737573477556-ac3ed2db618c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBidXNpbmVzc21hbiUyMG9mZmljZSUyMG1lZXRpbmd8ZW58MHx8fHwxNzgxMDc3NjE3fDA&ixlib=rb-4.1.0&q=85",
        quote:
            "The all-language summary is a game-changer for our team in Tier-2 cities. We finally understand every clause in our preferred language without hiring expensive consultants.",
    },
    {
        name: "Ankit Verma",
        role: "MSME Owner, Lucknow",
        image:
            "https://images.unsplash.com/photo-1577199001468-44c049e7603f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBjb25zdHJ1Y3Rpb24lMjB3b3JrZXIlMjBjb250cmFjdG9yJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc4MTA3NzYxN3ww&ixlib=rb-4.1.0&q=85",
        quote:
            "Risk analysis caught a 25% penalty clause we'd missed. We walked away from a bad tender and saved our company from a real disaster. Worth every rupee.",
    },
];

export const FAQS = [
    {
        q: "What is a tender analysis credit?",
        a: "One credit equals one complete tender analysis — covering summary, eligibility, EMD, deadlines, risks, and the all-language summary. Credits reset every month.",
    },
    {
        q: "Can I upgrade my plan anytime?",
        a: "Yes. You can upgrade, downgrade, or cancel anytime from your dashboard. Upgrades are pro-rated, and any unused credits roll over for 30 days.",
    },
    {
        q: "Is all language summary included?",
        a: "Yes — every plan, including Starter, includes summaries in all supported languages at no extra cost. Pick the language you prefer at the time of analysis.",
    },
    {
        q: "Can I download my reports?",
        a: "Yes. Every analysis can be exported as a formatted PDF report, ready to share with your team or partners.",
    },
    {
        q: "What happens when my credits finish?",
        a: "You can either wait for your next monthly cycle or upgrade to a higher plan instantly. Unused credits don't carry over between months on the Starter plan.",
    },
    {
        q: "Is there a free trial?",
        a: "Yes — every new account gets 2 free tender analyses to try the platform before subscribing. No credit card required.",
    },
    {
        q: "Is my tender data secure?",
        a: "All uploads are encrypted at rest and in transit. We never share your documents with third parties, and you can delete your data anytime.",
    },
    {
        q: "Do you support tenders from all states and ministries?",
        a: "Yes. TenderTrackAI supports central government tenders (GeM, CPPP), all state portals, PSU tenders, and private tenders.",
    },
];

export const RECENT_ANALYSES = [
    {
        id: "tta-2891",
        name: "Construction of 4-Lane Highway Bypass — Phase II",
        department: "Ministry of Road Transport & Highways",
        date: "12 Feb 2026",
        value: "₹48.5 Cr",
        status: "Eligible",
        matchScore: 87,
        risk: "Low",
    },
    {
        id: "tta-2890",
        name: "Supply of Medical Equipment for District Hospitals",
        department: "Health Department, Govt. of UP",
        date: "10 Feb 2026",
        value: "₹2.1 Cr",
        status: "Partially Eligible",
        matchScore: 62,
        risk: "Medium",
    },
    {
        id: "tta-2889",
        name: "Annual Maintenance Contract — Solar Power Plants",
        department: "MNRE, Government of India",
        date: "08 Feb 2026",
        value: "₹15.7 Cr",
        status: "Eligible",
        matchScore: 91,
        risk: "Low",
    },
    {
        id: "tta-2888",
        name: "Procurement of Office Furniture for Secretariat",
        department: "GAD, Govt. of Rajasthan",
        date: "05 Feb 2026",
        value: "₹85 Lakh",
        status: "Not Eligible",
        matchScore: 34,
        risk: "High",
    },
    {
        id: "tta-2887",
        name: "Smart City Surveillance Network Installation",
        department: "Bhopal Smart City Ltd.",
        date: "02 Feb 2026",
        value: "₹22.3 Cr",
        status: "Eligible",
        matchScore: 78,
        risk: "Medium",
    },
];

export const SAMPLE_ANALYSIS = {
    id: "tta-2891",
    name: "Construction of 4-Lane Highway Bypass — Phase II",
    department: "Ministry of Road Transport & Highways",
    tenderNo: "MORTH/2026/HW-NH48/0291",
    value: "₹48,50,00,000",
    emd: "₹48,50,000",
    emdMode: "Bank Guarantee / Online Payment",
    lastDate: "28 February 2026, 17:00 IST",
    publishedDate: "30 January 2026",
    eligibility: "Eligible",
    matchScore: 87,
    risk: "Low",
    summary:
        "This is a 4-lane bypass construction tender estimated at ₹48.5 Cr awarded by NHAI under MoRTH. The project spans 14.6 km in Rajasthan with a completion period of 24 months. The bidder must have completed at least one similar highway project of value ≥ ₹25 Cr in the last 5 years. Average annual turnover requirement is ₹30 Cr over the last 3 financial years. EMD of ₹48.5 lakh must be furnished as a Bank Guarantee valid for 180 days.",
    languageSummaryLabel: "हिंदी सारांश (Hindi sample)",
    languageSummary:
        "यह 14.6 किलोमीटर लंबे 4-लेन बायपास निर्माण की निविदा है, जिसका अनुमानित मूल्य ₹48.5 करोड़ है। NHAI द्वारा MoRTH के तहत जारी की गई इस परियोजना को 24 महीनों में पूरा करना होगा। बोली लगाने वाले को पिछले 5 वर्षों में कम से कम ₹25 करोड़ मूल्य की एक समान राजमार्ग परियोजना पूरी करनी चाहिए। पिछले 3 वित्तीय वर्षों में औसत वार्षिक टर्नओवर ₹30 करोड़ होना आवश्यक है। ₹48.5 लाख की EMD 180 दिनों के लिए वैध बैंक गारंटी के रूप में प्रस्तुत करनी होगी।",
    documents: [
        "Company Registration Certificate (GST + PAN)",
        "Average Annual Turnover Certificate (3 FY, CA certified)",
        "Past Experience Certificate — Similar work ≥ ₹25 Cr",
        "Solvency Certificate from Scheduled Bank (₹20 Cr)",
        "Power of Attorney for Authorized Signatory",
        "EMD: Bank Guarantee or Online Payment Proof",
        "Equipment Ownership Declaration",
        "Technical Personnel Affidavit",
        "GST Returns (Last 3 Years)",
        "ITR Returns (Last 3 Years)",
    ],
    risks: [
        {
            level: "Medium",
            title: "Liquidated Damages clause",
            desc: "0.5% of contract value per week of delay, capped at 10% of total contract value.",
        },
        {
            level: "Low",
            title: "Performance Security",
            desc: "5% of contract value to be deposited within 21 days of award. Standard for highway projects.",
        },
        {
            level: "Low",
            title: "Defect Liability Period",
            desc: "5 years from project completion. Within industry norms but requires long-term cash flow planning.",
        },
    ],
    nextAction:
        "You qualify for this tender. Begin preparing your Bank Guarantee for EMD immediately and gather the past experience certificate. Submit at least 48 hours before deadline to avoid last-minute portal issues.",
};

export const STATS = [
    { value: "10,000+", label: "Tenders Analyzed" },
    { value: "2 min", label: "Average Time Saved per Tender" },
    { value: "95%", label: "Faster Tender Understanding" },
    { value: "1,200+", label: "Active Indian Businesses" },
];

export const TRUST_LOGOS = [
    "NHAI",
    "PWD Rajasthan",
    "GeM",
    "CPPP",
    "Indian Railways",
    "MES",
    "NTPC",
    "BHEL",
];

export const CURRENT_USER = {
    name: "Arjun Mehta",
    email: "arjun@mehtaconstructions.in",
    company: "Mehta Constructions Pvt. Ltd.",
    plan: "Pro",
    creditsUsed: 18,
    creditsTotal: 30,
    avatar: "AM",
    renewsOn: "12 March 2026",
};
